import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
  useId,
} from "react";
import styles from "./ImageUpload.module.scss";
import refresh from "assets/icons/refresh.svg";
import close from "assets/icons/x-circle.svg";
import cn from "clsx";
import ImageWithFallback from "helpers/ImageWithFallback";
import { isMp4Media } from "helpers/isMp4Media";

type Props = {
  image?: string | File;
  onAddImage?: (e: File) => void;
  onDeleteImage?: () => void;
  hasDelete?: boolean;
  className?: string;
  size?: "default" | "large";
  children?: React.ReactNode;
  isActive?: boolean | number;
  hasError?: boolean;
  label?: string;
  labelClassName?: string;
  aspect?: string;
  type?: "portrait" | "landscape" | "square";
  disabled?: boolean;
  allowMp4?: boolean;
};

export type ImageUploadRef = {
  getImage: () => File | null;
  clearImage: () => void;
};

export const ImageUpload = forwardRef<{}, Props>(
  (
    {
      image,
      onAddImage,
      onDeleteImage,
      hasDelete = false,
      className,
      children,
      isActive,
      hasError,
      label,
      labelClassName,
      aspect,
      type,
      disabled = false,
      allowMp4 = false,
    },
    ref,
  ) => {
    const [img, setImg] = useState<string | null>(null);
    const [isVideo, setIsVideo] = useState(false);
    const fileRef = useRef<File | null>(null);
    const id = useId();

    const createImageUrl = (file: File) => {
      return URL.createObjectURL(file);
    };

    const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;

      const file = e.target.files?.[0] as File;

      if (!file) return;

      fileRef.current = file;
      setIsVideo(allowMp4 && isMp4Media(file));
      setImg(createImageUrl(file));
      onAddImage?.(file);

      e.target.value = "";
    };

    const handleRemoveImage = () => {
      const currentUrl = img as string;

      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
        fileRef.current = null;
        onDeleteImage?.();
        setImg(null);
        setIsVideo(false);
      }
    };

    const handleDeleteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (disabled) return;

      handleRemoveImage();
    };

    useImperativeHandle(ref, () => ({
      getImage: () => fileRef.current,
      clearImage: () => handleRemoveImage(),
    }));

    useEffect(() => {
      setIsVideo(allowMp4 && isMp4Media(image));

      if (typeof image === "string") {
        setImg(image);
      }

      if (image instanceof File) {
        setImg(createImageUrl(image));
      }

      return () => {
        if (typeof image === "string") {
          URL.revokeObjectURL(image);
        }
      };
    }, [allowMp4, image]);

    return (
      <div className={styles.container}>
        {label && (
          <span className={cn(styles.label, labelClassName)}>{label}</span>
        )}
        <div
          className={cn(styles.wrapper, "image-upload", className, {
            [styles.error]: hasError,
            [styles.disabled]: disabled,
          })}
        >
          <input
            type="file"
            accept={allowMp4 ? "image/*,video/mp4,.mp4" : "image/*"}
            onChange={handleAddImage}
            id={`image-upload-${id}`}
            disabled={disabled}
          />

          <label
            className={cn("image-upload-add", styles.action)}
            htmlFor={disabled ? undefined : `image-upload-${id}`}
            aria-disabled={disabled}
          >
            <img src={refresh} alt="" />
          </label>

          {img && (
            <div
              className={cn(
                "image-upload-preview game-item-img",
                styles.imagePreviewContainer,
                {
                  "no-active": !isActive,
                },
              )}
            >
              {/* <ImageWithFallback
                className={styles.imagePreview}
                src={img}
                alt="Preview"
              /> */}
              {isVideo ? (
                <video
                  className={cn(styles.imagePreview, {
                    [styles.noActive]: !isActive,
                  })}
                  src={img}
                  controls
                  muted
                  playsInline
                />
              ) : (
                <ImageWithFallback
                  className={cn(styles.imagePreview, {
                    [styles.noActive]: !isActive,
                  })}
                  src={img}
                  alt="Preview"
                />
              )}
              {children}
            </div>
          )}

          {!img &&
            (type ? (
              <div className={cn(styles.placeholder, "image-upload-placeholder")}>
                <div className={cn(styles.shape, styles[type || "square"])} />
                <div className={styles.info}>
                  <span className={styles.aspect}>{aspect}</span>
                </div>
              </div>
            ) : (
              <p className="image-upload-noimage-text">No image selected</p>
            ))}

          {img && hasDelete && !disabled && (
            <button
              className={cn("image-upload-delete", styles.delete)}
              onClick={handleDeleteClick}
              disabled={disabled}
            >
              <img src={close} alt="Delete Icon" />
            </button>
          )}
        </div>
      </div>
    );
  },
);
